import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/lib/lenis";
import { asset } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const ROTATE_MS = 6000;

const HeroSlides = ({ images }) => {
  const [i, setI] = useState(0);
  const n = images.length;
  useEffect(() => {
    if (n < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), ROTATE_MS);
    return () => clearInterval(t);
  }, [n]);
  if (!n) return null;
  const src = images[i % n];
  return (
    <>
      <AnimatePresence initial={false}>
        <motion.img key={`${src}-${i}`} data-testid="hero-image" src={asset(src)} alt="Featured artwork"
          initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: ROTATE_MS / 1000 + 1.6, ease: "linear" } }}
          className="absolute inset-0 w-full h-full object-cover" />
      </AnimatePresence>
      {n > 1 && (
        <div className="absolute bottom-3 right-3 z-[3] flex gap-1.5" data-testid="hero-slide-dots">
          {images.map((_, k) => (
            <button key={k} data-testid={`hero-dot-${k}`} onClick={() => setI(k)} aria-label={`Show image ${k + 1}`}
              className={`h-1.5 transition-[width,background-color] duration-500 ${k === i % n ? "w-6 bg-[#F5A524]" : "w-3 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      )}
    </>
  );
};

const HeroArt = ({ rx, ry, y, images, label }) => (
  <motion.div style={{ y, perspective: 1200 }} className="relative w-full max-w-[460px] mx-auto lg:ml-auto">
    <div className="absolute -inset-16 bg-[radial-gradient(closest-side,rgba(245,165,36,.28),transparent)] blur-2xl pointer-events-none" />
    <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.5, ease: EASE }} className="relative">
      <div className="absolute inset-0 bg-[#F5A524] clip-corner" style={{ transform: "translateZ(-60px) translate(18px,18px)" }} />
      <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
        className="relative clip-corner scanlines aspect-[4/5] max-h-[62vh] lg:max-h-[72vh] w-full overflow-hidden bg-[#12131A]">
        <HeroSlides images={images} />
      </motion.div>
      {label && <div data-testid="hero-image-label" style={{ transform: "translateZ(70px)" }} className="absolute -left-4 sm:-left-8 bottom-10 bg-[#0B0C10] border border-[#F5A524] px-3 py-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524]">
        {label}
      </div>}
      <div style={{ transform: "translateZ(50px)" }} className="absolute -right-2 top-6 bg-[#FF2A55] text-black px-2 py-1 font-mono text-[11px] font-bold tracking-widest">
        REC ●
      </div>
    </motion.div>
  </motion.div>
);

export const Hero = ({ site }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 110, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-screen pt-[100px] pb-16 px-5 sm:px-10 lg:px-16 overflow-hidden grain grid-lines">
      <div className="absolute top-[88px] left-5 sm:left-10 font-mono text-[11px] tracking-[0.3em] text-zinc-400">SYS://EMPORIO_v2.0</div>
      <div className="absolute top-[88px] right-5 sm:right-10 font-mono text-[11px] tracking-[0.3em] text-zinc-400 hidden sm:block">SIGNAL: <span className="text-[#F5A524]">LIVE</span></div>

      <div className="relative grid lg:grid-cols-12 gap-14 lg:gap-8 items-center min-h-[calc(100vh-180px)] pt-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-xs tracking-[0.35em] text-[#F5A524] mb-6">// VISUAL ART · COMICS · VIGNETTES</motion.p>
          <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.82] tracking-tight text-[clamp(3.6rem,9.5vw,10rem)]">
            <MaskLine delay={0.15} className="text-white">Lawrence's</MaskLine>
            <MaskLine delay={0.32}>
              <span className="glitch text-[#F5A524]" data-text="Emporio">Emporio</span>
            </MaskLine>
          </h1>
          <MaskLine delay={0.55} className="mt-8 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">
            A street-level archive of ink, paint and panels. Comics, vignettes and art pieces pulled from the city's after-hours.
          </MaskLine>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-view-works-btn" onClick={() => scrollToId("works")}
              className="group clip-tab bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] uppercase px-7 py-4 flex items-center gap-3 hover:bg-white transition-colors">
              Enter the archive <ArrowDownRight size={18} className="transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-contact-btn" onClick={() => scrollToId("contact")}
              className="font-mono text-sm tracking-[0.2em] uppercase px-7 py-4 border border-[#F5A524]/40 text-[#F5A524] hover:border-[#F5A524] hover:bg-[#F5A524]/10 transition-colors">
              Get in touch
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 relative">
          <HeroArt rx={rx} ry={ry} y={artY} images={site ? (site.hero_images?.length ? site.hero_images : ["/img/hero.jpg"]) : []} label={site ? site.hero_label : ""} />
        </div>
      </div>

      <div className="absolute bottom-6 left-5 sm:left-10 font-mono text-[11px] tracking-[0.3em] text-zinc-400 hidden lg:flex items-center gap-3">
        <span className="inline-block w-10 h-px bg-[#F5A524]" /> SCROLL
      </div>
    </section>
  );
};
